/**
 * Where releases live.
 *
 * NOT the platform repository. The platform's source is private; a release has to be
 * fetchable by an install that holds no credentials at all — the gateway sends no token
 * by construction — so the channel, the companion archives and everything else an install
 * reads anonymously are published to a separate public repository.
 *
 * Every URL below is built from this one constant, and that is the point rather than a
 * tidiness preference. A builder keeping its own literal would go on addressing a
 * repository the public cannot read, and the failure is silent: a 404 is classified as
 * "channel not public yet" and the checker goes dormant. An installation would stop being
 * offered updates and say nothing about it.
 */
export const RELEASES_REPO = "m8t-labs/m8t-releases";

const RELEASES_BASE = `https://github.com/${RELEASES_REPO}/releases`;

export const CHANNEL_LATEST_URL = `${RELEASES_BASE}/latest/download/manifest.json`;

/**
 * The rolling pointer tag the canary channel publishes under.
 *
 * A channel is a POINTER to a version, not a place — the npm dist-tag model
 * (`latest`, `next`) and the one half of GitHub uses for `nightly`. GitHub owns
 * exactly one such pointer, `latest`, and deliberately excludes pre-releases
 * from it; that pointer belongs to customers. The canary needs its own, so it
 * gets a release tag that is re-pointed on every build.
 *
 * Every canary build ALSO publishes an immutable `platform-v<version>` tag. That
 * is not redundancy: the health gate resolves a rollback target BY VERSION, so a
 * build that only ever existed under a moving pointer could not be rolled back
 * to. The pointer answers "what is newest"; the version tag answers "what was
 * build N", and both questions get asked.
 */
export const CANARY_POINTER_TAG = "canary";

/** Where a canary install's channel URL points. Spelled here with every other
 *  release URL, so the one-host guard keeps covering it. */
export const CHANNEL_CANARY_URL = `${RELEASES_BASE}/download/${CANARY_POINTER_TAG}/manifest.json`;

/**
 * Normalize ANY platform version form — bare (`0.2.0`), v-prefixed (`v0.2.0`),
 * or a full tag (`platform-v0.2.0`) — to the canonical release TAG
 * (`platform-v0.2.0`). This is the ONE place the tag/bare distinction is
 * resolved: every per-version release URL must route through it, so a caller
 * that only has a bare version (e.g. the rollback path's
 * `previousPlatformVersion`) can never hand-build a `…/download/0.2.0/…` URL
 * that 404s against the tag-keyed release asset.
 */
export function platformTag(version: string): string {
  const bare = version.trim().replace(/^platform-/, "").replace(/^v/, "");
  return `platform-v${bare}`;
}

export function channelUrlForVersion(version: string): string {
  return `${RELEASES_BASE}/download/${platformTag(version)}/manifest.json`;
}

/**
 * A file attached to a release, addressed by the release's own tag. Components
 * released on their own cadence — the companions are the first — carry their
 * tag in the manifest that pins them, rather than deriving it from the
 * platform's.
 */
export function releaseAssetUrl(tag: string, asset: string): string {
  return `${RELEASES_BASE}/download/${tag}/${asset}`;
}
